// src/components/resumo/ApostilaDraftsModal.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { X, User, Clock, CheckCheck, Eye, FileText, MessageSquare } from 'lucide-react';
import { ActiveDraftContext } from './ReviewerContextBar';
import './ApostilaDraftsModal.css';

interface PageNote {
    pageId: string;
    note: string;
    status: 'revised' | 'attention' | 'untouched';
}

interface Draft {
    _id: string;
    authorName: string;
    updatedAt: string;
    status: 'pending' | 'seen' | 'applied';
    pages: any[];
    quizzes?: any;
    reviewNote?: string;
    pageNotes?: PageNote[];
}

interface Props {
    deckId: string;
    subdeckId: string;
    darkMode: boolean;
    onClose: () => void;
    onLoadDraft: (context: ActiveDraftContext) => void;
}

const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const draftStatusLabel: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Pendente', cls: 'adm-status-pending' },
    seen:    { label: 'Visto',    cls: 'adm-status-seen' },
    applied: { label: 'Aplicado', cls: 'adm-status-applied' },
};

const PAGE_STATUS_COLORS: Record<string, string> = {
    revised:   '#16a34a',
    attention: '#d97706',
    untouched: '#9ca3af',
};

const PAGE_STATUS_BG: Record<string, string> = {
    revised:   '#f0fdf4',
    attention: '#fffbeb',
    untouched: '#f3f4f6',
};

const PAGE_STATUS_LABEL: Record<string, string> = {
    revised:   'Revisada',
    attention: 'Atenção',
    untouched: 'Intocada',
};

// Tooltip simples com hover
const PageBadge: React.FC<{ num: number; status: string; note: string }> = ({ num, status, note }) => {
    const [hover, setHover] = useState(false);
    const color = PAGE_STATUS_COLORS[status] ?? PAGE_STATUS_COLORS.untouched;
    const bg    = PAGE_STATUS_BG[status]    ?? PAGE_STATUS_BG.untouched;

    return (
        <span
            className="adm-page-badge"
            style={{ background: bg, color, border: `1px solid ${color}33` }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {num}
            {hover && note && (
                <span className="adm-page-tooltip">{note}</span>
            )}
        </span>
    );
};

const ApostilaDraftsModal: React.FC<Props> = ({ deckId, subdeckId, darkMode, onClose, onLoadDraft }) => {
    const token = localStorage.getItem('accessToken');
    const [drafts, setDrafts] = useState<Draft[]>([]);
    const [loading, setLoading] = useState(true);
    const [patching, setPatching] = useState<string | null>(null);

    const fetchDrafts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/apostila-draft/${deckId}/${subdeckId}/admin`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) setDrafts(data.drafts);
        } finally {
            setLoading(false);
        }
    }, [deckId, subdeckId, token]);

    useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

    const patchStatus = async (draftId: string, status: 'seen' | 'applied') => {
        setPatching(draftId);
        try {
            await fetch(`/api/apostila-draft/${draftId}/status`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            });
            setDrafts(prev => prev.map(d => d._id === draftId ? { ...d, status } : d));
        } finally {
            setPatching(null);
        }
    };

    const handleLoad = async (draft: Draft) => {
        if (draft.status === 'pending') {
            await patchStatus(draft._id, 'seen');
        }
        const ctx = {
            draftId:    draft._id,
            authorName: draft.authorName,
            updatedAt:  draft.updatedAt,
            reviewNote: draft.reviewNote,
            pageNotes:  draft.pageNotes,
            pages:      draft.pages ?? [],
            quizzes:    draft.quizzes,
        };
        console.log('[ApostilaDraftsModal] onLoadDraft ctx:', {
            reviewNote: ctx.reviewNote,
            pageNotesCount: ctx.pageNotes?.length ?? 0,
            pagesCount: ctx.pages.length,
        });
        onLoadDraft(ctx);
        onClose();
    };

    // Monta a lista de status por página: páginas sem entrada no pageNotes ficam como 'untouched'
    const buildPageStatuses = (draft: Draft) => {
        return draft.pages.map((page, idx) => {
            const pageNote = draft.pageNotes?.find(n => n.pageId === page.id);
            return {
                num: idx + 1,
                status: pageNote?.status ?? 'untouched',
                note: pageNote?.note ?? '',
            };
        });
    };

    return (
        <div className="adm-overlay" onClick={onClose}>
            <div className={`adm-modal ${darkMode ? 'dark' : ''}`} onClick={e => e.stopPropagation()}>
                <div className="adm-modal-header">
                    <h3 className="adm-modal-title">
                        <FileText size={16} />
                        Sugestões de revisores
                    </h3>
                    <button className="adm-modal-close" onClick={onClose}><X size={15} /></button>
                </div>

                <div className="adm-modal-body">
                    {loading ? (
                        <div className="adm-loading"><div className="adm-spinner" /> Carregando rascunhos...</div>
                    ) : drafts.length === 0 ? (
                        <div className="adm-empty">Nenhum rascunho enviado ainda.</div>
                    ) : (
                        <div className="adm-list">
                            {drafts.map(draft => {
                                const pageStatuses = buildPageStatuses(draft);
                                return (
                                    <div key={draft._id} className={`adm-card ${draft.status === 'applied' ? 'adm-card-applied' : ''}`}>
                                        <div className="adm-card-top">
                                            <div className="adm-card-info">
                                                <span className="adm-card-author">
                                                    <User size={12} /> {draft.authorName}
                                                </span>
                                                <span className="adm-card-date">
                                                    <Clock size={12} /> {fmtDate(draft.updatedAt)}
                                                </span>
                                            </div>
                                            <span className={`adm-status-badge ${draftStatusLabel[draft.status].cls}`}>
                                                {draftStatusLabel[draft.status].label}
                                            </span>
                                        </div>

                                        {/* Nota geral da revisão */}
                                        {draft.reviewNote && draft.reviewNote.trim() !== '' && (
                                            <div className="adm-review-note">
                                                <MessageSquare size={12} />
                                                <span>{draft.reviewNote}</span>
                                            </div>
                                        )}

                                        {/* Badges de status por página */}
                                        {pageStatuses.length > 0 && (
                                            <div className="adm-pages-row">
                                                {pageStatuses.map(({ num, status, note }) => (
                                                    <PageBadge key={num} num={num} status={status} note={note} />
                                                ))}
                                                <span className="adm-pages-legend">
                                                    {(['revised', 'attention', 'untouched'] as const).map(s => (
                                                        <React.Fragment key={s}>
                                                            <span style={{ color: PAGE_STATUS_COLORS[s] }}>■</span>{' '}
                                                            {PAGE_STATUS_LABEL[s]}{' '}
                                                        </React.Fragment>
                                                    ))}
                                                </span>
                                            </div>
                                        )}

                                        <div className="adm-card-actions">
                                            <button
                                                className="adm-btn-load"
                                                onClick={() => handleLoad(draft)}
                                                disabled={patching === draft._id}
                                            >
                                                <Eye size={13} />
                                                Carregar no editor
                                            </button>
                                            {draft.status !== 'applied' && (
                                                <button
                                                    className="adm-btn-applied"
                                                    onClick={() => patchStatus(draft._id, 'applied')}
                                                    disabled={patching === draft._id}
                                                >
                                                    <CheckCheck size={13} />
                                                    Marcar aplicado
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApostilaDraftsModal;
